import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Badge, Button, DataTable, JsonDiffViewer, type ColumnDef } from '@shared-ui/components';
import { adminApi, type AgentRun, type AgentPersonaVersion } from '../../api/adminApi';
import { PersonaForm } from './PersonaForm';
import './PersonaAgents.css';

export function PersonaAgentPersonaDetail() {
  const { personaId } = useParams();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [openVersion, setOpenVersion] = useState<number | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['agent-persona', personaId],
    queryFn: () => adminApi.getAgentPersona(personaId!),
    enabled: !!personaId,
  });

  if (isLoading) return <div className="settings-section">Loading…</div>;

  const persona = data?.persona;
  const spec = persona?.spec;
  const runs = data?.runs ?? [];
  const history = [...(persona?.history ?? [])].reverse(); // newest first

  const columns: ColumnDef<AgentRun>[] = [
    { key: 'started', header: 'Started', render: r => (r.startedAt ? format(new Date(r.startedAt), 'MMM d HH:mm') : '—') },
    { key: 'status', header: 'Status', render: r => <Badge variant="default" size="sm">{r.status}</Badge> },
    { key: 'findings', header: 'Findings', className: 'pa-num', render: r => (r.perPersona ?? []).reduce((n, p) => n + (p.feedbackCount ?? 0), 0) || '—' },
  ];

  return (
    <div className="settings-section">
      <div className="section-header">
        <Button variant="ghost" size="sm" onClick={() => navigate('/persona-agents')}>← Back</Button>
        <h1 className="pa-mono">{personaId}</h1>
        {spec && (
          <div className="pa-header-actions">
            {persona?.version != null && <span className="pa-muted">v{persona.version}</span>}
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Edit</Button>
          </div>
        )}
      </div>
      {spec ? (
        <div className="pa-spec">
          <div><strong>Role:</strong> {spec.role}</div>
          <div><strong>Board / Grade:</strong> {spec.board} · grade {spec.grade}</div>
          <div><strong>Subjects:</strong> {(spec.subjects ?? []).join(', ')}</div>
          <div><strong>Skill:</strong> {spec.skillLevel} · <strong>Patience:</strong> {spec.patience} · <strong>Effort:</strong> {spec.effort ?? '—'} · <strong>Device:</strong> {spec.device}</div>
          <div><strong>Entry:</strong> {spec.entry ?? 'login'}{spec.freVariants ? ' · rotates FRE variants' : ''}</div>
          <div><strong>Personality:</strong> {spec.personality}</div>
          <div><strong>Goal:</strong> {spec.goalTemplate}</div>
        </div>
      ) : <p className="pa-muted">No spec stored for this persona.</p>}

      {history.length > 0 && (
        <>
          <h2 className="pa-h2">History ({history.length})</h2>
          <ul className="pa-history">
            {history.map((h: AgentPersonaVersion) => (
              <li key={h.version} className="pa-history-item">
                <button className="pa-shot-toggle" onClick={() => setOpenVersion(openVersion === h.version ? null : h.version)}>
                  {openVersion === h.version ? '▼' : '▶'} v{h.version}
                </button>
                <span className="pa-muted">
                  {h.at ? format(new Date(h.at), 'MMM d HH:mm') : '—'} · superseded · by {h.updatedBy}
                </span>
                {openVersion === h.version && spec && (
                  <div className="pa-history-diff">
                    <JsonDiffViewer before={h.spec} after={spec} title={`v${h.version} → v${persona?.version} (current)`} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <h2 className="pa-h2">Runs ({runs.length}) · {data?.findingsCount ?? 0} findings</h2>
      <DataTable<AgentRun>
        columns={columns}
        data={runs}
        rowKey={r => r.id}
        onRowClick={r => navigate(`/persona-agents/runs/${encodeURIComponent(r.runId)}`)}
        emptyMessage="No runs"
      />

      {editing && spec && <PersonaForm initial={spec} onClose={() => setEditing(false)} />}
    </div>
  );
}
